/* METTA product page reviews (Judge.me widget, 30 Oct 2026).
   Judge.me renders the reviews (a Vue app in #judgeme_product_reviews); this
   adds what its "minimal" layout leaves out and puts it in Icelandic:
   - a summary of our own beside the reviews (desktop) or over them (phones):
     the title, stars filled to the average, the average and the count, and
     a bar for each star, from the data Judge.me prints with the widget
     (jdgm.data.reviewWidget[product id]). Its own title and average are
     hidden (metta-pdp.css, REVIEWS).
   - its English words (Verified, Anonymous, the sort options, the store's
     reply, read more, write a review, the page controls) and its dates
     in Icelandic, by what each element is rather than what it says, and
     dates from each review's own timestamp; kept so through re-renders.
   - a review whose text only repeats its title shows the title alone; one
     whose title is the start of its text (cut off) shows the text alone.
   - after a colour swap (metta-pdp-siblings.js) the reviews section comes in
     new and empty: its inline data script runs and Judge.me's entry module
     runs again, which mounts the new product's widget.
   - the stars by the price (snippets/product-block-header.liquid) scroll to
     the reviews. */
(() => {
  if (window.mettaReviews) return;
  window.mettaReviews = true;

  const WIDGET = '#judgeme_product_reviews';
  const MONTHS = ['jan.', 'feb.', 'mar.', 'apr.', 'maí', 'jún.', 'júl.', 'ágú.', 'sep.', 'okt.', 'nóv.', 'des.'];
  // The sort options by their values, not by Judge.me's wording (which can
  // be edited in its admin).
  const SORT = {
    created_at: 'Nýjast',
    most_recent: 'Nýjast',
    highest_rating: 'Hæsta einkunn',
    lowest_rating: 'Lægsta einkunn',
    with_pictures: 'Aðeins með myndum',
    pictures_first: 'Myndir fyrst',
    videos_first: 'Myndbönd fyrst',
    most_helpful: 'Gagnlegast',
    most_relevant: 'Mest viðeigandi',
    verified_only: 'Aðeins staðfest kaup',
  };

  // 1 umsögn, 21 umsögn; 2, 11 and 12 umsagnir.
  const reviews = (n) => `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'umsögn' : 'umsagnir'}`;
  const average = (value) => Number(value).toFixed(1);

  // Text set in the node Vue holds, so its own updates still land (and are
  // caught again by the observer below).
  const setText = (element, text) => {
    if (!element || !text || element.textContent.trim() === text) return;
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node && !node.nodeValue.trim()) node = walker.nextNode();
    if (node) node.nodeValue = text;
    else element.textContent = text;
  };

  // Attributes are not watched by the observer below; set only when they
  // differ all the same.
  const setLabel = (element, name, value) => {
    if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
  };

  const dateText =(day, month, year) => `${day}. ${MONTHS[month - 1]} ${year}`;
  // A review's date from its data: the real time, whatever date format is
  // set in Judge.me. Iceland is on UTC all year, so the UTC day is the day.
  const fromData = (stamp) => {
    const date = new Date(stamp);
    return Number.isNaN(date.getTime()) ? '' : dateText(date.getUTCDate(), date.getUTCMonth() + 1, date.getUTCFullYear());
  };
  // A date shown without its review's data (a later page, a new sort): read
  // in the order Judge.me's own setting names (mm/dd/yyyy, dd/mm/yyyy,
  // yyyy-mm-dd …); anything else is left as it is.
  const fromText = (text) => {
    const format = String(window.jdgmSettings?.review_date_format || 'mm/dd/yyyy').toLowerCase();
    const keys = format.split(/[^dmy]+/).filter(Boolean).map((key) => key[0]);
    const parts = text.split(/[^0-9]+/).filter(Boolean).map(Number);
    if (keys.length !== 3 || parts.length !== 3 || new Set(keys).size !== 3) return '';
    const value = {};
    keys.forEach((key, index) => { value[key] = parts[index]; });
    const year = value.y < 100 ? 2000 + value.y : value.y;
    const check = new Date(Date.UTC(year, value.m - 1, value.d));
    return check.getUTCMonth() === value.m - 1 && check.getUTCDate() === value.d ? dateText(value.d, value.m, year) : '';
  };

  // In Icelandic by what each element is, not by what it says: Judge.me's
  // texts can be edited in its admin, and its date format changed.
  const translate = (widget) => {
    const known = new Map();
    Object.values(window.jdgm?.data?.reviewWidget || {}).forEach((data) => {
      (data.reviews || []).forEach((review) => known.set(review.uuid, review));
    });
    const anonymousName = [window.jdgmSettings?.widget_reviewer_anonymous, 'Anonymous'].filter(Boolean);

    widget.querySelectorAll('select option').forEach((option) => setText(option, SORT[option.value]));
    widget.querySelectorAll('.jm-sort-filter label').forEach((label) => setText(label, 'Raða eftir'));
    widget.querySelectorAll('.jm-verified-buyer-badge__text').forEach((badge) => setText(badge, 'Staðfest kaup'));
    // The rest of the widget (2 Nov 2026): the write-review button, "read
    // more", the store's reply, photos, and the page controls.
    widget.querySelectorAll('.jm-action-buttons__button[data-testid="write-review-button"]').forEach((button) => setText(button, 'Skrifa umsögn'));
    widget.querySelectorAll('.jm-review-content__expand-button, .jm-shop-reply__expand-button').forEach((button) => setText(button, 'Lesa meira'));
    const shop = window.jdgmSettings?.reply_name_text || 'Metta';
    widget.querySelectorAll('.jm-shop-reply__author').forEach((author) => setText(author, `Svar frá ${shop}`));
    widget.querySelectorAll('.jm-media-thumbnail-list__thumbnail').forEach((thumb) => {
      const video = Boolean(thumb.querySelector('.jm-media-thumbnail-list__play-icon'));
      setLabel(thumb, 'aria-label', video ? 'Spila myndband' : 'Skoða mynd');
      setLabel(thumb.querySelector('img'), 'alt', video ? 'Myndband með umsögn' : 'Mynd með umsögn');
    });
    widget.querySelectorAll('.jm-pagination-controls').forEach((nav) => {
      setLabel(nav, 'aria-label', 'Síður');
      const [previous, next] = nav.querySelectorAll('.jm-pagination-controls__button--nav');
      setLabel(previous, 'aria-label', 'Fyrri síða');
      setLabel(next, 'aria-label', 'Næsta síða');
      nav.querySelectorAll('.jm-pagination-controls__button--page').forEach((page) => setLabel(page, 'aria-label', `Síða ${page.textContent.trim()}`));
    });
    widget.querySelectorAll('.jm-load-more-pagination').forEach((nav) => {
      setLabel(nav, 'aria-label', 'Fleiri umsagnir');
      const button = nav.querySelector('.jm-button');
      setText(button, 'Sýna fleiri umsagnir');
      setLabel(button, 'aria-label', 'Sýna fleiri umsagnir');
    });
    widget.querySelectorAll('.jm-review-item').forEach((item) => {
      const review = known.get(item.getAttribute('review-uuid'));
      const name = item.querySelector('.jm-reviewer-info__name');
      if (name && (review?.is_anonymous_reviewer || anonymousName.includes(name.textContent.trim()))) setText(name, 'Nafnlaus');
      const when = item.querySelector('.jm-reviewer-info__details .jm-text:not(.jm-reviewer-info__name):not(.jm-verified-buyer-badge__text)');
      if (when) setText(when, review?.created_at ? fromData(review.created_at) : fromText(when.textContent.trim()));
    });
  };

  const words = (text) => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const dedupe = (root) => {
    root.querySelectorAll('.jm-review-content').forEach((content) => {
      const title = words(content.querySelector('.jm-review-content__title')?.textContent || '');
      const body = words(content.querySelector('.jm-review-content__body')?.textContent || '');
      const same = Boolean(title) && title === body;
      content.classList.toggle('metta-review--title-only', same);
      content.classList.toggle('metta-review--body-only', !same && Boolean(title) && body.startsWith(title));
    });
  };

  const summary = (widget) => {
    const block = widget.parentElement;
    const data = window.jdgm?.data?.reviewWidget?.[widget.dataset.productId];
    if (!block || !data || !data.number_of_reviews) return;
    block.classList.add('metta-reviews');
    block.id = block.id || 'umsagnir';
    if (block.querySelector(':scope > .metta-reviews__summary')) return;

    const rating = Number(data.average_rating) || 0;
    const bars = (data.histogram || [])
      .slice()
      .sort((a, b) => b.rating - a.rating)
      .map((row) => `
        <li class="metta-reviews__bar">
          <span class="metta-reviews__bar-label">${row.rating} <span aria-hidden="true">★</span><span class="visually-hidden">${row.rating === 1 ? 'stjarna' : 'stjörnur'}</span></span>
          <span class="metta-reviews__bar-track" aria-hidden="true"><span style="width: ${Number(row.percentage) || 0}%"></span></span>
          <span class="metta-reviews__bar-count">${row.frequency}</span>
        </li>`)
      .join('');

    const element = document.createElement('div');
    element.className = 'metta-reviews__summary';
    element.innerHTML = `
      <h2 class="metta-reviews__title">Umsagnir viðskiptavina</h2>
      <p class="metta-reviews__score">
        <span class="metta-stars" style="--rating: ${rating}" role="img" aria-label="${average(rating)} af 5 stjörnum"></span>
        <span class="metta-reviews__average">${average(rating)}</span>
        <span class="metta-reviews__count">${reviews(data.number_of_reviews)}</span>
      </p>
      ${bars ? `<ul class="metta-reviews__bars">${bars}</ul>` : ''}`;
    block.insertBefore(element, widget);
  };

  // No reviews: Judge.me leaves the widget without elements (hide_widget);
  // the section goes too (metta-pdp.css).
  const mark = (widget) => {
    widget.closest('.shopify-section')?.classList.toggle('metta-reviews-empty', !widget.firstElementChild);
  };

  const tidy = (widget) => {
    mark(widget);
    summary(widget);
    translate(widget);
    dedupe(widget);
  };

  const watched = new WeakSet();
  const watch = (widget) => {
    if (watched.has(widget)) return;
    watched.add(widget);
    let queued = false;
    // Our own text changes are mutations too; they settle, as each change
    // only turns English into Icelandic once.
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; tidy(widget); });
    }).observe(widget, { childList: true, subtree: true, characterData: true });
    tidy(widget);
  };

  const setup = (root = document) => root.querySelectorAll(WIDGET).forEach(watch);

  // A colour swap brings the reviews section in as fresh markup: run its
  // data script, then Judge.me's entry module again (a new query, so it runs
  // instead of coming from the module cache); it mounts every widget it does
  // not know yet.
  const remount = (section) => {
    const widget = section.querySelector(WIDGET);
    // The section arrives with Judge.me's server-rendered copy of the
    // reviews inside; the Vue app marks the widget once it has mounted.
    if (!widget || widget.hasAttribute('data-v-app')) return;
    section.querySelectorAll('script:not([src])').forEach((old) => {
      const script = document.createElement('script');
      Array.from(old.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
    // A colour with no reviews: nothing for Judge.me to mount (it throws on
    // the missing data), so the section just goes (2 Nov 2026).
    const data = window.jdgm?.data?.reviewWidget?.[widget.dataset.productId];
    if (!data || !data.number_of_reviews) {
      (widget.closest('.shopify-section') || section).classList.add('metta-reviews-empty');
      return;
    }
    const entry = document.querySelector('script[type="module"][src*="review_widget"]');
    if (!entry) return;
    const url = new URL(entry.src);
    url.searchParams.set('metta', Date.now());
    import(url.href).catch((error) => console.error(error));
    watch(widget);
    // Its server copy goes once Vue mounts; with no reviews nothing mounts.
    setTimeout(() => mark(widget), 3000);
  };

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    if (section?.querySelector?.(WIDGET)) remount(section);
  });

  // The stars by the price: to the reviews, under the pinned header.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('.metta-rating-link');
    if (!link) return;
    const target = document.querySelector('.metta-reviews') || document.querySelector(WIDGET);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup());
  else setup();
  // Judge.me fills the widget after load; the observer above is set on the
  // container, which is in the markup from the start.
})();
